import { Controller, useForm } from 'react-hook-form';
import { IconButton, InputAdornment, Stack } from '@mui/material';
import Tooltip from '@mui/material/Tooltip';
import ClearIcon from '@mui/icons-material/Clear';
import { useEffect, useRef } from 'react';
import { useIntl } from 'react-intl';
import PropTypes from 'prop-types';
import ExpandableTextArea from 'components/ExpandableTextArea';
import MDInput from 'components/MDInput';

export function PromptNodeForm({
  id,
  data = {
    description: '',
    content: ''
  },
  onUpdateData
}) {
  const { formatMessage: msg } = useIntl();
  const prevDescriptionRef = useRef(data?.description || '');
  const prevContentRef = useRef(data?.content || '');

  const { control, setValue, watch } = useForm({
    defaultValues: {
      description: data?.description || '',
      content: data?.content || ''
    }
  });

  const formValues = watch();

  useEffect(() => {
    const descriptionChanged = formValues.description !== prevDescriptionRef.current;
    const contentChanged = formValues.content !== prevContentRef.current;

    if (descriptionChanged || contentChanged) {
      // Update refs before calling onNodeChange to prevent multiple updates
      prevDescriptionRef.current = formValues.description;
      prevContentRef.current = formValues.content;

      const updatedData = {
        ...data,
        description: formValues.description,
        content: formValues.content
      };

      onUpdateData({
        id,
        data: updatedData
      });
    }
  }, [formValues, data, onUpdateData, id]);

  const clearField = (fieldName) => {
    setValue(fieldName, '');
  };

  return (
    <Stack spacing={1} sx={{ p: 1 }}>
      <Controller
        name="description"
        control={control}
        render={({ field }) => (
          <MDInput
            {...field}
            label={msg({ id: 'field.description.label' })}
            variant="filled"
            fullWidth
            size="small"
            InputProps={{
              endAdornment: (
                <InputAdornment position="end">
                  <Tooltip title={msg({ id: 'tooltip.clear' })}>
                    <IconButton onClick={() => clearField('description')}>
                      <ClearIcon />
                    </IconButton>
                  </Tooltip>
                </InputAdornment>
              )
            }}
          />
        )}
      />

      <Controller
        name="content"
        control={control}
        render={({ field }) => (
          <ExpandableTextArea
            field={field}
            clearField={() => clearField('content')}
            label={msg({ id: 'field.content.label' })}
            control={control}
            minRows={20}
            maxRows={30}
          />
        )}
      />
    </Stack>
  );
}

PromptNodeForm.propTypes = {
  id: PropTypes.string,
  data: PropTypes.shape({
    id: PropTypes.string,
    label: PropTypes.string,
    description: PropTypes.string,
    content: PropTypes.string
  }),
  onUpdateData: PropTypes.func.isRequired
};
